import React, { useState } from "react";
import { Table, Button, Space, Modal, Typography } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import { getCompanyForms } from "../../../../api/server";
import { createForm, updateForm, deleteForm } from "../../settingsSlice";
import FormModal from "./FormModal";
import Surface from "../../../../components/layouts/Surface";
import FormHeaderWrapper from "../../../../components/forms/FormHeader.style";
import { FormattedMessage } from "react-intl";
import RestrictedComponent from "../../../../components/util/RestrictedComponent";
import { useIntl } from "react-intl";
import PDFFieldMapperModal from "../../../../components/companyforms/PDFFieldMapperModal";

const Forms = ({ activekey }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [pdfMapperVisible, setPdfMapperVisible] = useState(false);
  const { Title } = Typography;
  const intl = useIntl();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // React Query for fetching forms with stale time to avoid unnecessary refetches
  const {
    data: response,
    isLoading,
    error,
  } = useQuery("forms", getCompanyForms, {
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Cache persists for 30 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
  });
  // Use items array from the response
  const forms = response?.data?.items || [];

  const entitiesMapping = [
    {
      value: "patient",
      label: intl.formatMessage({ id: "app.settings.forms.patientLabel" }),
    },
    {
      value: "employee",
      label: intl.formatMessage({ id: "app.settings.forms.employeeLabel" }),
    },
    {
      value: "company",
      label: intl.formatMessage({ id: "app.settings.forms.companyLabel" }),
    },
  ];

  const columns = [
    {
      title: intl.formatMessage({ id: "app.settings.forms.name" }),
      dataIndex: "name",
      key: "name",
    },
    {
      title: intl.formatMessage({ id: "app.settings.forms.description" }),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: "app.settings.forms.entities" }),
      dataIndex: "entities",
      key: "entities",
      render: (entities) =>
        entities
          ?.map(
            (entity) =>
              entitiesMapping.find((mapping) => mapping.value === entity)?.label
          )
          ?.join(", "),
    },
    {
      title: intl.formatMessage({ id: "app.settings.forms.actions" }),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const handleEdit = (form) => {
    setEditingForm(form);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: "app.settings.forms.deleteConfirmationTitle",
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: "app.settings.forms.deleteConfirmationContent",
      }),
      onOk: async () => {
        await dispatch(deleteForm(id));
        return queryClient.invalidateQueries("forms");
        // Delete functionality not implemented yet
      },
      onCancel() {},
    });
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingForm(null);
  };

  // Add new method to open modal for new form
  const handleCreate = () => {
    setEditingForm(null); // Ensure editingForm is null for new forms
    setIsModalVisible(true);
  };

  const handleFormSubmit = async (values) => {
    if (editingForm) {
      await dispatch(updateForm({ ...editingForm, ...values }));
    } else {
      await dispatch(createForm(values));
    }
    queryClient.invalidateQueries("forms");
    handleModalClose();
  };

  const handlePDFConfig = (config) => {
    if (!editingForm) return;

    const formattedConfig = {
      ...(editingForm.config || {}),
      pdfMapping: config.map((field) => ({
        entity: editingForm.entity,
        field: field.name,
        x: field.x,
        y: field.y,
        page: field.page,
      })),
    };

    dispatch(
      updateForm({
        ...editingForm,
        config: formattedConfig,
      })
    );
  };

  const handleClosePDFMapper = () => {
    setPdfMapperVisible(false);
  };

  if (error) {
    return <div>Error loading forms: {error.message}</div>;
  }
  return (
    <Surface>
      <FormHeaderWrapper>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            <FormattedMessage id={`app.settings.forms`} />
          </Title>
          <Space>
            <Button onClick={() => setPdfMapperVisible(true)}>
              {intl.formatMessage({
                id: "app.settings.forms.configure_pdf_fields",
              })}
            </Button>
            <RestrictedComponent
              resource={"organization.settings"}
              action={"editor"}
              comp={null}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate} // Changed from setIsModalVisible(true)
              >
                <FormattedMessage id={`app.actions.CreateNewCompanyForm`} />
              </Button>
            </RestrictedComponent>
          </Space>
        </div>
      </FormHeaderWrapper>

      <Table
        columns={columns}
        dataSource={forms}
        rowKey="id"
        loading={isLoading}
      />

      <FormModal
        key={editingForm?.id || "new"} // Add key to force remount
        visible={isModalVisible}
        onCancel={handleModalClose}
        onSubmit={handleFormSubmit}
        initialValues={editingForm || null} // Explicitly pass null for new forms
        loading={false}
        onPDFConfig={handlePDFConfig}
      />

      <PDFFieldMapperModal
        visible={pdfMapperVisible}
        onCancel={handleClosePDFMapper}
        onSave={(config) => {
          handleClosePDFMapper();
        }}
        title={intl.formatMessage({ id: "app.settings.forms.configurePDF" })}
      />
    </Surface>
  );
};

export default Forms;
